import { UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DeactivateUserDialog from "./DeactivateUserDialog";
import EditUserDialog from "./EditUserDialog";
import type { User } from "@/generated/types";

interface UsersTableProps {
  users: User[];
  loading: boolean;
  onDeactivate: (id: string) => void;
  onActivate: (id: string) => void;
  onEditSuccess: () => void;
}

export default function UsersTable({ users, loading, onDeactivate, onActivate, onEditSuccess }: UsersTableProps) {
  return (
    <TooltipProvider>
      <div className="mb-16">
        <h2 className="text-lg font-light tracking-tight text-primary-950 dark:text-white mb-4">
          Todos os Usuários
        </h2>
        <div className="bg-white dark:bg-primary-900 border border-primary-200 dark:border-primary-800">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary-200 dark:border-primary-800">
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 font-normal">
                    Nome
                  </th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 font-normal">
                    E-mail
                  </th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 font-normal">
                    Função
                  </th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 font-normal">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 font-normal">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-primary-100 dark:border-primary-800/50 last:border-0"
                  >
                    <td className="px-6 py-4 text-sm text-primary-900 dark:text-white">
                      {user.fullName}
                    </td>
                    <td className="px-6 py-4 text-sm text-primary-600 dark:text-primary-400">
                      {user.email}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-3 py-1 text-xs uppercase tracking-widest border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs uppercase tracking-widest border ${
                          user.disabled
                            ? "border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                            : "border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                        }`}
                      >
                        <>
                          <UserCheck className="h-3 w-3" />
                          {user.disabled ? 'Desativado' : 'Ativo'}
                        </>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <EditUserDialog user={user} onSuccess={onEditSuccess} />
                        {user.disabled ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onActivate(user.id)}
                                aria-label="Ativar usuário"
                                className="h-8 w-8 p-0 text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Ativar usuário</TooltipContent>
                          </Tooltip>
                        ) : (
                          <DeactivateUserDialog
                            onConfirm={() => onDeactivate(user.id)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && !loading && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-primary-600 dark:text-primary-400">
                      Nenhum usuário encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
